import React, { useEffect, useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { Company, Customer, Industry, Sector, User } from "../../../../core";
import { number } from "currency-codes";
import MergePhone from "../merge-phone";
import MergeEmail from "../merge-email";
import { DropdownList } from "react-widgets";
import { format } from "date-fns";
import MergeField, { IMergeModel } from "../merge-field";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import api from "../../../../api";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { useDataLoader } from "react-remote-data-hooks";
import MergeUserField, { CompanyWithUser } from "../merge-user-field";

interface IMergeFormProps {
  firstName: string;
  lastName: string;
  middleName: string;
  phones: Phone[];
  emails: Email[];
  dob: string;
  ageRange: string;
  gender: string;
  addresses: Address[];
}

interface Address {
  isPrimary: boolean;
  value: string;
  type: string;
}

interface Email {
  isPrimary: boolean;
  email: string;
  type: string;
}

interface Phone {
  isPrimary: boolean;
  phone: string;
  type: string;
}

interface MergeReviewCustomerProps {
  onReload?: () => void;
  onClose: () => void;
  onBack: () => void;
  className?: string;
  company: Company;
  mergingCompanies: Company[];
}

const MergeReviewCompany: React.FunctionComponent<MergeReviewCustomerProps> = ({
  onClose,
  onBack,
  className,
  company,
  mergingCompanies,
}) => {
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyWithUser[]>([]);

  const { control, handleSubmit, errors, watch , trigger } = useForm<any>({
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const onSubmit = async (data: IMergeFormProps) => {
    const companyDataForMerge: Customer = {
      ...data, 
      phones: data?.phones?.map((item) => {
        return {
          phone: item?.phone,
          isPrimary: item?.isPrimary,
          phoneType: item?.type,
        };
      }),
      emails: data?.emails?.map((item) => {
        return {
          email: item?.email,
          isPrimary: item?.isPrimary,
          emailType: item?.type,
        };
      }),
      addresses: data?.addresses?.map((item) => {
        return {
          address: item?.value,
          isPrimary: item?.isPrimary,
          addressType: item?.type || "other" ,
          // added default Other type for bypassing validation of backend 
        };
      }),
    };
    const mergingCompanyIds = mergingCompanies?.map((item) => {
      return item?._id ?? "";
    });
    try {
      const response = await api.company.mergeCompany(
        company?._id!,
        mergingCompanyIds,
        companyDataForMerge
      );
      if (response?.status === 200) {
        toastr.success("Success", `Companies merged successfully !`);
        onClose?.();
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response.data?.message ?? `Some errors occured while merging`
      );
    }
  };
  
  const phoneContacts = [company, ...mergingCompanies]
    ?.filter((item) => (item?.phones !== undefined && item?.phones !== null && item?.phones !== '' as any))
    ?.map((item) => {
      if (item?.phones && item?.phones?.length > 0) {
        return {
          name: item?.name,
          numbers:
            item?.phones?.map((ph) => {
              return {
                phone: ph.phone,
                phoneType: ph.phoneType,
                isPrimary: ph.isPrimary,
              };
            }) ?? [],
        };
      }
    })
    .filter(Boolean);
  const emailContacts = [company, ...mergingCompanies]
    ?.filter((item) => (item?.emails !== undefined && item?.emails !== null && item?.emails !== '' as any))
    ?.map((item) => {
      if (item?.emails && item?.emails?.length > 0) {
        return {
          name: item?.name,
          emails:
            item?.emails?.map((ph) => {
              return {
                email: ph.email,
                emailType: ph.emailType,
                isPrimary: ph.isPrimary,
              };
            }) ?? [],
        };
      }
    })
    .filter(Boolean);

  const address = [company, ...mergingCompanies]
    ?.filter((item) => (item?.addresses !== undefined && item?.addresses !== null && item?.addresses !== '' as any))
    ?.map((item) => {
      if (item?.addresses && item?.addresses?.length > 0) {
        return {
          title: item?.name,
          items:
            item?.addresses.filter(ph=>ph.address)?.map((ph) => {
              return {
                value: ph.address ?? "",
                type: ph.addressType ?? "",
                isPrimary: ph.isPrimary ?? "",
              };
            }) ?? [],
        };
      }
    })
    .filter(item=> item && item?.items?.length > 0);

  const companyPan = [company, ...mergingCompanies]
  ?.filter((item) => (item?.companyPan !== undefined && item?.companyPan !== null && item?.companyPan !== '' as any))
    ?.map((item) => {
      if (item?.companyPan !== undefined) {
        return {
          name: item?.name,
          label: item?.companyPan,
          value: item?.companyPan,
        };
      }
    })
    .filter(Boolean);

  const scale = [company, ...mergingCompanies]
  ?.filter((item) => (item?.sector !== undefined && item?.sector !== null && item?.sector !== '' as any))
    ?.map((item) => {
      if (item?.sector !== undefined) {
        return {
          name: item?.name,
          label: item?.sector?.name,
          value: item?.sector?._id,
        };
      }
    })
    .filter(Boolean);

    const authorisedCapital = [company, ...mergingCompanies]
    ?.filter((item) => (item?.authorisedCapital !== undefined && item?.authorisedCapital !== null && item?.authorisedCapital !== '' as any))
    ?.map((item) => {
      if (item?.authorisedCapital !== undefined) {
        return {
          name: item?.name,
          label: item?.authorisedCapital,
          value: item?.authorisedCapital,
        };
      }
    })
    .filter(Boolean);

    const employeesInIndia = [company, ...mergingCompanies]
    ?.filter((item) => (item?.employeesInIndia !== undefined && item?.employeesInIndia !== null && item?.employeesInIndia !== '' as any))
    ?.map((item) => {
      if (item?.employeesInIndia !== undefined) {
        return {
          name: item?.name,
          label: item?.employeesInIndia,
          value: item?.employeesInIndia,
        };
      }
    })
    .filter(Boolean);

    const gstinNumber = [company, ...mergingCompanies]
    ?.filter((item) => (item?.gstinNumber !== undefined && item?.gstinNumber !== null && item?.gstinNumber !== '' as any))
    ?.map((item) => {
      if (item?.gstinNumber !== undefined) {
        return {
          name: item?.name,
          label: item?.gstinNumber,
          value: item?.gstinNumber,
        };
      }
    })
    .filter(Boolean);

    const industry = [company, ...mergingCompanies]
    ?.filter((item) => (item?.industry !== undefined && item?.industry !== null && item?.industry !== '' as any))
    ?.map((item) => {
      if (item?.industry !== undefined) {
        return {
          name: item?.name,
          label: item?.industry?.name,
          value: item?.industry?._id,
        };
      }
    })
    .filter(Boolean);

    const cinNumber = [company, ...mergingCompanies]
    ?.filter((item) => (item?.cinNumber !== undefined && item?.cinNumber !== null && item?.cinNumber !== '' as any))
    ?.map((item) => {
      if (item?.cinNumber !== undefined) {
        return {
          name: item?.name,
          label: item?.cinNumber,
          value: item?.cinNumber,
        };
      }
    })
    .filter(Boolean);

    const originCountry = [company, ...mergingCompanies]
    ?.filter((item) => (item?.originCountry !== undefined && item?.originCountry !== null && item?.originCountry !== '' as any))
    ?.map((item) => {
      if (item?.originCountry !== undefined) {
        return {
          name: item?.name,
          label: item?.originCountry,
          value: item?.originCountry,
        };
      }
    })
    .filter(Boolean);

    const incorporatedDate = [company, ...mergingCompanies]
    ?.filter((item) => (item?.incorporatedDate !== undefined && item?.incorporatedDate !== null && item?.incorporatedDate !== '' as any))
    ?.map((item) => {
      if (item?.incorporatedDate !== undefined) {
        return {
          name: item?.name,
          label: moment(item?.incorporatedDate).format("DD MMM YYYY"),
          value: item?.incorporatedDate,
        };
      }
    })
    .filter(Boolean);

    const websiteUrl = [company, ...mergingCompanies]
    ?.filter((item) => (item?.websiteUrl !== undefined && item?.websiteUrl !== null && item?.websiteUrl !== '' as any))
    ?.map((item) => {
      if (item?.websiteUrl !== undefined) {
        return {
          name: item?.name,
          label: item?.websiteUrl,
          value: item?.websiteUrl,
        };
      }
    })
    .filter(Boolean);

    const fetchCompanyUsers = async () => {
      try {
        const companyUsrs = await Promise.all(
          [...mergingCompanies].map(async (companyDetailsData) => {
            const people = await api.company.getCompanyPeoples(companyDetailsData._id || '');
            return {
              title: companyDetailsData.name,
              companyDetails: companyDetailsData,
              items: people,
            };
          })
        );
        setCompanyUsers(companyUsrs.filter(compny => compny.items.length > 0))
      } catch (error) {
        // console.error('Error fetching company users:', error);
      }
    };

    useEffect(() => {
      fetchCompanyUsers();
    }, [])
    
  // const address = [
  //   {
  //     Address: "1234 Market Street, Suite 567 Metropolis, 560077 Bangalore",
  //     id: 30,
  //   },
  //   {
  //     Address: "5678 Elm Avenue, Apt 101 Metro City, 500067 Bangalore",
  //     id: 31,
  //   },
  //   {
  //     Address: "7890 Pine Drive, Floor 2 Metro City, 400089 Bangalore",
  //     id: 32,
  //   },
  // ];
  const renderDropdown = ({ item }: any) => (
    <>
      <div className="py-2">
        <div className="mb-1 text-dark font-weight-semibold">{item.label}</div>
        <span className="text-muted font-xxs font-italic d-block font-weight-normal">
          {item.name}
        </span>
      </div>
    </>
  );

  const renderCompanyMergeConfirmation = () => {
    return confirmation ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          style={{
            zIndex: 9999,
            background: "#00000047",
          }}
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                      Are you sure you want to merge the selected companies to the main company ?
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => setConfirmation(false)}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        // onClick={() => onSubmit()}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  return (
    <div className="modal-open customer-info-merge">
      <div
        className="detail-popup bg-white position-fixed top-0 w-100 left-0 h-100 overflow-auto in"
        style={{ zIndex: 9999 }}
      >
        <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-99">
          <div className="row align-items-center">
            <div
              className="col-auto"
              onClick={() => {
                onClose?.();
              }}
            >
              <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="col">
              <h5 className="mb-0 py-2">Review and Merge</h5>
            </div>
          </div>
        </div>
        <div className={`${className || ""} h-100`}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row h-100 mx-0">
              <div className="col-sm-4 col-12  border-right px-0 merge-lead-left-wrap customer-merge-left-wrap">
                <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                  <h5 className="font-normal font-weight-bold mb-0">
                    Company Info
                  </h5>
                </div>

                <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  {/* <form onSubmit={handleSubmit(onSubmit)}> */}
                  {/* Select Input */}
                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                       Name<span className="text-danger">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="name"
                      rules={{
                        required: {
                          value: true,
                          message: "Name is required",
                        },
                      }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          {...restProps}
                          data={[company, ...mergingCompanies]
                            ?.filter(
                              (item) =>
                                item?.name !== "" &&
                                item?.name !== undefined
                            )
                            ?.map((item) => {
                              return {
                                label: item?.name,
                                value: item?.name,
                              };
                            })
                          }
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          // itemComponent={renderDropdown}

                          onChange={(val) => {
                            onChange(val?.value);
                          }}
                        />
                      )}
                      // render={(field) => (
                      //   <select className="form-control" {...field}>
                      //     {[customer, ...mergingCustomers].map((option) => (
                      //       <option
                      //         key={option.firstName}
                      //         value={option.firstName}
                      //       >
                      //         {option.firstName}
                      //       </option>
                      //     ))}
                      //   </select>
                      // )}
                    />
                    {errors?.name?.message && (
                      <ErrorMessage className="align-self-start">
                        {errors?.name?.message}
                      </ErrorMessage>
                    )}
                  </div>
                  
                  {phoneContacts.length ? <div className="customer-details-info my-4">
                    <h3 className="font-normal font-weight-semibold mb-2">
                      Mobile Number
                    </h3>
                    <Controller
                      control={control}
                      name="phones"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergePhone
                          name={name}
                          onUpdate={onChange}
                          contacts={phoneContacts}
                          {...restProps}
                        />
                      )}
                    />
                  </div> : null}

                  {emailContacts.length ? <div className="customer-details-info my-4">
                    <h3 className="font-normal font-weight-semibold mb-2">
                      Email
                    </h3>
                    <Controller
                      control={control}
                      name="emails"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergeEmail
                          name={name}
                          onUpdate={onChange}
                          contacts={emailContacts}
                          {...restProps}
                        />
                      )}
                    />
                  </div> :null}

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Company Pan
                    </label>
                    <Controller
                      control={control}
                      name="companyPan"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={companyPan}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Scale
                    </label>
                    <Controller
                      control={control}
                      name="scale"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={scale}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Authorized Capital
                    </label>
                    <Controller
                      control={control}
                      name="authorisedCapital"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={authorisedCapital}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Employees In India
                    </label>
                    <Controller
                      control={control}
                      name="employeesInIndia"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={employeesInIndia}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      GSTIN Number
                    </label>
                    <Controller
                      control={control}
                      name="gstinNumber"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={gstinNumber}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Industry
                    </label>
                    <Controller
                      control={control}
                      name="industry"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={industry}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Country of Origin
                    </label>
                    <Controller
                      control={control}
                      name="originCountry"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={originCountry}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      CIN Number{cinNumber?.length ? <span className="text-danger">*</span> : null}
                    </label>
                    <Controller
                      control={control}
                      name="cinNumber"
                      rules={{required: {
                        value: cinNumber?.length ? true : false,
                        message: "CIN Number is required",
                      }, }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={cinNumber}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                    {errors?.cinNumber?.message && (
                      <ErrorMessage className="align-self-start">
                        {errors?.cinNumber?.message}
                      </ErrorMessage>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Incorporation Date
                    </label>
                    <Controller
                      control={control}
                      name="incorporatedDate"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={incorporatedDate}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <label className="font-normal font-weight-semibold mb-1">
                      Website
                    </label>
                    <Controller
                      control={control}
                      name="websiteUrl"
                      // rules={{ required: true }}
                      render={({ onChange, ...restProps }) => (
                        <DropdownList
                          data={websiteUrl}
                          textField={"label"}
                          valueField={"value"}
                          placeholder={"Select"}
                          itemComponent={renderDropdown}
                          {...restProps}
                          onChange={(val) => {
                            onChange(val.value);
                          }}
                        />
                      )}
                    />
                  </div>

                  {/* </form> */}
                </div>
              </div>
              <div className="col-sm-4 col-12 px-0 merge-lead-right-wrap  border-right">
                <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="font-normal font-weight-bold mb-0">
                      Users ({companyUsers.map(item=>item.items).flat()?.length})
                    </h5>
                  </div>
                </div>
                <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  <div className="customer-details-info mb-4 border-bottom pb-2">
                    <Controller
                      control={control}
                      name="users"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergeUserField
                          name={name}
                          onUpdate={onChange}
                          data={companyUsers}
                          {...restProps}
                          showType={true}
                        />
                      )}
                    />
                  </div>
                </div>
                
              </div>
              <div className="col-sm-4 col-12 px-0 merge-lead-right-wrap  border-right">
                <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="font-normal font-weight-bold mb-0">
                      Addresses ({address?.length})
                    </h5>
                  </div>
                </div>
                <div className="px-4 pt-3" style={{ paddingBottom: "110px" }}>
                  <div className="customer-details-info mb-4 border-bottom pb-2">
                    <Controller
                      control={control}
                      name="addresses"
                      // rules={{ required: true }}
                      render={({ onChange, name, ...restProps }) => (
                        <MergeField
                          name={name}
                          onUpdate={onChange}
                          data={address}
                          {...restProps}
                          showType={true}
                        />
                      )}
                    />
                  </div>
                </div>
                
              </div>
              <div className="button-bottom-merge d-flex justify-content-end left-0 right-0">
                <button className="btn btn-outline-dark px-5" type="button" onClick={()=> onBack?.()}>Back</button>

                <button type="button" className="btn btn-primary px-4" onClick={()=>{
                  trigger().then((hasNoError) => {
                    if(hasNoError){
                      if(watch('phones')?.length || watch('emails')?.length){
                        setConfirmation(true)
                      }else{
                        toastr.error('Error', 'Please Select At least One Email or Mobile Number')
                      }
                      
                    }
                  })
                  }}>
                  Review & Confirm
                </button>
              </div>
              {/* </form> */}
            </div>
            {renderCompanyMergeConfirmation()}
          </form>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default MergeReviewCompany;
